import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

const BotMessageManager = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    message_type: '',
    category: '',
    reply_message: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // โหลดข้อมูลเมื่อ Component ถูก mount
  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('bot_messages')
        .select('*')
        .order('id', { ascending: true });
      
      if (error) throw error;
      console.log('Fetched data:', data);
      setMessages(data);
    } catch (error) {
      console.error('Error fetching messages:', error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        // Update existing record
        const { error } = await supabase
          .from('bot_messages')
          .update(formData)
          .eq('id', editingId);
        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('bot_messages')
          .insert([formData]);
        if (error) throw error;
      }
      
      // Reset form and reload data
      setFormData({ message_type: '', category: '', reply_message: '' });
      setEditingId(null);
      setIsModalOpen(false);
      fetchMessages();
      alert(editingId ? 'Updated successfully' : 'Added successfully');
    } catch (error) {
      console.error('Error saving message:', error.message);
      alert('Error saving message: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (message) => {
    setFormData({
      message_type: message.message_type || '',
      category: message.category || '',
      reply_message: message.reply_message || ''
    });
    setEditingId(message.id);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id) => {
      setDeleteId(id);
      setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('bot_messages')
        .delete()
        .eq('id', deleteId);
      
      if (error) throw error;
      fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error.message);
      alert('Error deleting message: ' + error.message);
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      setDeleteId(null);
    }
  };

  const handleCancel = () => {
    setFormData({ message_type: '', category: '', reply_message: '' });
    setEditingId(null);
    setIsModalOpen(false);
  };

  const openAddModal = () => {
    setFormData({ message_type: '', category: '', reply_message: '' });
    setEditingId(null);
    setIsModalOpen(true);
  };

  // คำนวณสถิติแยกตามหมวดหมู่
  const stats = messages.reduce((acc, msg) => {
    const cat = msg.category || 'Uncategorized';
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  // กรองข้อมูลตาม Search Term และ Category ที่เลือก
  const filteredMessages = messages.filter(msg => {
    const matchesSearch = (msg.reply_message || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (msg.message_type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (msg.category || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory ? msg.category === selectedCategory : true;
    return matchesSearch && matchesCategory;
  });

  return (
    <div style={{ padding: '40px', backgroundColor: 'var(--color-bg)', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h2 style={{ margin: 0, color: 'var(--color-primary)', fontWeight: '700', fontSize: '28px' }}>Bot Message Manager</h2>
        <button 
          onClick={openAddModal}
          style={{
            padding: '12px 24px',
            backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            boxShadow: '0 4px 6px rgba(var(--color-primary-rgb), 0.3)',
            transition: 'all 0.2s ease'
          }}
        >
          + Add New
        </button>
      </div>

      {error && <div style={{ color: 'red', marginBottom: '10px', padding: '10px', backgroundColor: '#ffebee', borderRadius: '5px' }}>Error: {error}</div>}
      
      {/* Dashboard / Stats Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
        <div 
          onClick={() => setSelectedCategory(null)}
          style={{ 
            backgroundColor: selectedCategory === null ? 'var(--color-primary)' : 'var(--color-primary)', 
            color: 'white', 
            padding: '24px', 
            borderRadius: '16px', 
            boxShadow: '0 8px 16px rgba(var(--color-primary-rgb), 0.15)',
            cursor: 'pointer',
            transform: selectedCategory === null ? 'scale(1.02)' : 'scale(1)',
            transition: 'all 0.2s'
          }}
        >
          <h3 style={{ margin: '0 0 5px 0', fontSize: '14px', opacity: 0.9 }}>Total Messages</h3>
          <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>{messages.length}</p>
        </div>
        {Object.entries(stats).map(([category, count]) => (
          <div 
            key={category} 
            onClick={() => setSelectedCategory(category === selectedCategory ? null : category)}
            style={{ 
              backgroundColor: category === selectedCategory ? 'var(--color-primary-soft)' : '#fff', 
              padding: '24px', 
              borderRadius: '16px', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)', 
              border: category === selectedCategory ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <h3 style={{ margin: '0 0 5px 0', fontSize: '14px', color: 'var(--color-text-muted)' }}>{category}</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: 'var(--color-text)' }}>{count}</p>
          </div>
        ))}
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: '30px' }}>
        <input
          type="text"
          placeholder="🔍 Search by Type, Category, or Message..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: '12px',
            border: '1px solid var(--color-border)',
            fontSize: '16px',
            boxSizing: 'border-box',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
            outlineColor: 'var(--color-primary)'
          }}
        />
      </div>

      {/* Modal Form Section */}
      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '32px', borderRadius: '20px', width: '90%', maxWidth: '500px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
            <form onSubmit={handleSubmit}>
              <h3 style={{ marginTop: 0, marginBottom: '24px', color: 'var(--color-text)', fontSize: '24px' }}>{editingId ? 'Edit Message' : 'Add New Message'}</h3>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>Message Type:</label>
                <input type="text" name="message_type" value={formData.message_type} onChange={handleInputChange} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border)', boxSizing: 'border-box', fontSize: '15px' }} required />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>Category:</label>
                <input type="text" name="category" value={formData.category} onChange={handleInputChange} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border)', boxSizing: 'border-box', fontSize: '15px' }} required />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>Reply Message:</label>
                <textarea name="reply_message" value={formData.reply_message} onChange={handleInputChange} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border)', minHeight: '120px', boxSizing: 'border-box', fontSize: '15px', resize: 'vertical' }} required />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" onClick={handleCancel} style={{ padding: '10px 20px', cursor: 'pointer', border: '1px solid var(--color-border)', borderRadius: '8px', backgroundColor: '#fff', color: 'var(--color-text-muted)', fontWeight: '500' }}>Cancel</button>
                <button type="submit" disabled={loading} style={{ padding: '10px 24px', backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  {loading ? 'Saving...' : (editingId ? 'Update' : 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* List Section (Table Style) */}
      {filteredMessages.length === 0 && !loading ? (
        <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', marginTop: '20px' }}>
          ไม่พบข้อมูล (No messages found).
        </p>
      ) : (
        <div style={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden', border: '1px solid var(--color-border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: 'var(--color-primary-soft)', borderBottom: '2px solid var(--color-primary-soft)' }}>
              <tr>
                <th style={{ padding: '20px', textAlign: 'left', color: 'var(--color-primary)', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</th>
                <th style={{ padding: '20px', textAlign: 'left', color: 'var(--color-primary)', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</th>
                <th style={{ padding: '20px', textAlign: 'left', color: 'var(--color-primary)', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Reply Message</th>
                <th style={{ padding: '20px', textAlign: 'right', color: 'var(--color-primary)', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMessages.map((msg) => (
                <tr key={msg.id} style={{ borderBottom: '1px solid var(--color-surface-muted)', transition: 'background-color 0.2s' }}>
                  <td style={{ padding: '20px' }}>
                    <span style={{ backgroundColor: 'var(--color-primary-soft)', color: 'var(--color-primary)', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', border: '1px solid #C8E6C9' }}>
                      {msg.message_type}
                    </span>
                  </td>
                  <td style={{ padding: '20px' }}>
                    <span style={{ backgroundColor: '#E0F2F1', color: '#00695C', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', border: '1px solid #B2DFDB' }}>
                      {msg.category}
                    </span>
                  </td>
                  <td style={{ padding: '20px', color: 'var(--color-text)', fontSize: '15px', lineHeight: '1.6' }}>{msg.reply_message}</td>
                  <td style={{ padding: '20px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button 
                      onClick={() => handleEdit(msg)} 
                      style={{ marginRight: '8px', padding: '8px', cursor: 'pointer', backgroundColor: '#fff', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                      title="Edit"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(msg.id)} 
                      style={{ padding: '8px', cursor: 'pointer', backgroundColor: '#FFEBEE', color: '#D32F2F', border: '1px solid #FFCDD2', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                      title="Delete"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isDeleteModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2200 }}>
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-text)' }}>Confirm Delete</h3>
                <p style={{ color: 'var(--color-text-muted)', marginBottom: '25px' }}>Are you sure you want to delete this message?</p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                    <button 
                        onClick={() => setIsDeleteModalOpen(false)}
                        style={{ padding: '10px 20px', backgroundColor: '#fff', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={confirmDelete}
                        style={{ padding: '10px 20px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(244, 67, 54, 0.3)' }}
                    >
                        {loading ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default BotMessageManager;
